
import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { RouterModule } from "@angular/router";
import { BookingModule } from "@fry/booking/app";
import { BookingsStore } from "@fry/booking/lib/bookings";
import { FryCommonModule } from "@fry/components/common";
import { InteractiveTableModule } from "@fry/components/common/list/interactive-table";
import { InteractiveTableOptions } from "@fry/components/common/list/interactive-table/interactive-table.component";
import { MaterialModule } from "@fry/components/material";
import { ListDefaults, TableService } from "@fry/lib/list";
import { UnboxedModule } from "@fry/ui";
import { tap } from "rxjs";
import { BookingTableCardOptions } from "./booking-table.card";
import { ReactiveFormsModule, Validators } from "@angular/forms";
import { EasFieldControl, EasFieldType, EasFormGroup, EasFormService } from "@fry/components/common/easform";
import { UserService } from "@fry/lib/users";
import { AuthService } from "@fry/lib/auth";
import { EasFormModule } from "@fry/components/common/easform/easform.module";
import { BoardCard } from "@fry/boards/lib";

@Component({
    selector: "eas-booking-table-edit-card",
    templateUrl: "./booking-table-card.settings.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    RouterModule,
    FryCommonModule,
    UnboxedModule,
    BookingModule,
    InteractiveTableModule,
    MaterialModule,
    ReactiveFormsModule,
    EasFormModule
],
    providers: [
        TableService
    ]
})
export class BookingTableCardSettingsComponent {

    dialogRef = inject(MatDialogRef<BookingTableCardSettingsComponent>);
    data: { options: BookingTableCardOptions, card: BoardCard } = inject(MAT_DIALOG_DATA);
    options = this.data.options;
    card = this.data.card;

    private tableService = inject(TableService);
    private bookingsStore = inject(BookingsStore);
    private userService = inject(UserService, { optional: true });
    private auth = inject(AuthService);

    private formService = inject(EasFormService);
    private formFields: EasFieldControl[] = [
        {
            id: 'title',
            title: $localize `Title`,
            type: EasFieldType.Field,
            validators: [Validators.required],
            widget: { type: 'string' },
        },
        {
            id: 'pageSize',
            title: $localize `Items per page`,
            type: EasFieldType.Field,
            validators: [Validators.required],
            options: {
                categories: [5, 10, 20, 50, 100].map(itm => ({ id: itm, name: itm.toString() }))
            },
            widget: { type: 'select' },
        }
    ];

    public form!: EasFormGroup;

    $searchModel = this.tableService.$currentSearchModel;

    tableLoaded = signal<boolean>(false);

    public tableComponentOptions: InteractiveTableOptions = {
        enableColumns: true,
        enableFilters: true,
        enablePagination: true,
        enableControls: true,
        itemSingleLabel: $localize `booking`,
        itemPluralLabel: $localize `bookings`,
        layout: 'table'
      };

    constructor() {
    }

    ngOnInit() {
        const initial = {
            title: this.options.title,
            pageSize: this.options.pageSize || 5,
        }
        this.form = this.formService.createForm(this.formFields, initial)
        this.initializeTable().subscribe({
            error: (error) => {
                console.error(error);
            }
        });
    }

    initializeTable() {
        const pageSize = this.form.getValue().pageSize || 5;
        const defaults: ListDefaults = { listType: 'table', engine: 'es', pageSize: pageSize };
        defaults.fixedSearchModel = {
            filter: {
            },
            sort: { sort_on: 'createdDate', sort_order: 'desc' },
            select: ['user', 'bookingOption', 'createdDate', 'preview'],
            options: {
                includeFormFields: false
            },
            context: {
                filter: {},
                user: this.userService ? this.userService.user.doc.username : this.auth.current.username
            },
        }

        defaults.searchModel = this.options.searchModel;
        return this.tableService.setupWith(this.bookingsStore, defaults, 'general')
            .pipe(tap(() => this.tableLoaded.set(true)));
    }

    onCancel() {
        this.dialogRef.close();
    }

    onSave() {
        const searchModel = this.$searchModel();
        if (searchModel === undefined) {
            throw new Error('Search model is undefined');
        }

        const options: BookingTableCardOptions = {
            title: this.form.getValue().title,
            pageSize: this.form.getValue().pageSize,
            searchModel,
        }
        this.dialogRef.close(options);
    }

    onUpdate() {
        this.initializeTable().subscribe({
            error: (error) => {
                console.error(error);
            }
        });
    }
}
